import { usePathname } from 'next/navigation';
import { useCallback, useMemo } from 'react';

import {
  OUT_OF_STOCK,
  RING_SIZES,
  SELECT_SIZE,
  SIZING_KIT,
} from '@/app/[locale]/product/_consts';
import {
  type PDPContextType,
  usePDPContext,
} from '@/app/[locale]/product/_contexts/PDPProvider';
import { ACCESSORIES_ROOT_PATH, POP_PATHNAME } from '@/app/consts/pathnames';
import { RingModelEnum } from '@/app/enums/RingModelEnum';
import useOuraGen4Enabled from '@/app/hooks/useOuraGen4Enabled';
import type { ChargerModel } from '@/app/types/accessory';
import type { RingFinish, RingStyle } from '@/app/types/ring';

import type CurrentRouteValues from './types';

interface UseCurrentRouteParams {
  readonly accessory: string | undefined;
  readonly locale: string;
}

const EXCLUDED_SEGMENTS = ['', 'product', 'rings', 'accessories', 'oura-gen3'];

const useCurrentRoute = ({
  accessory: accessoryParam,
  locale,
}: UseCurrentRouteParams): CurrentRouteValues => {
  const { getStyleStockStatus, getFinishStockStatus }: PDPContextType =
    usePDPContext();
  const gen4Enabled = useOuraGen4Enabled();

  const pathname = usePathname();
  const segments = pathname
    .split('/')
    .filter((segment) => ![...EXCLUDED_SEGMENTS, locale].includes(segment));
  const routes = useMemo(() => ({ ...segments }), [segments]);
  const isAccessory = useMemo(() => Boolean(accessoryParam), [accessoryParam]);
  const validAccessoryRoutes = useMemo(
    () =>
      gen4Enabled ? ['charger-oura-4', 'charger-oura-3'] : ['charger-set'],
    [gen4Enabled],
  );
  const isOuraRing4Wizard = useMemo(
    () => gen4Enabled && routes[0] === RingModelEnum.Oura4,
    [gen4Enabled, routes],
  );

  // check style
  const style = useMemo(() => {
    const styleValue = !isAccessory && routes[0] ? routes[0] : '';
    const value = styleValue as RingStyle;
    const isValid =
      getStyleStockStatus({
        style: styleValue as RingStyle,
        isOuraRing4: isOuraRing4Wizard,
      }) !== OUT_OF_STOCK;

    return {
      value,
      isValid,
    };
  }, [getStyleStockStatus, isAccessory, isOuraRing4Wizard, routes]);

  // check finish
  const finish = useMemo(() => {
    const finishValue = !isAccessory && routes[1] ? routes[1] : '';
    const value = finishValue as RingFinish;
    const isValid =
      getFinishStockStatus({
        style: style.value,
        finish: finishValue as RingFinish,
        isOuraRing4: isOuraRing4Wizard,
      }) !== OUT_OF_STOCK && style.isValid;

    return {
      value,
      isValid,
    };
  }, [
    getFinishStockStatus,
    isAccessory,
    isOuraRing4Wizard,
    routes,
    style.isValid,
    style.value,
  ]);

  // check size
  const size = useMemo(() => {
    const accessoryValue = isAccessory ? routes[1] : '';
    const validAccessoryDefault = accessoryValue === SELECT_SIZE;
    const sizeValue = routes[2] ?? accessoryValue ?? '';
    const isValid =
      sizeValue === SIZING_KIT ||
      validAccessoryDefault ||
      RING_SIZES.includes(sizeValue);

    return {
      value: sizeValue,
      isValid,
    };
  }, [isAccessory, routes]);

  // check accessory (charger-set)
  const accessory = useMemo(() => {
    const accessoryValue = (
      isAccessory && routes[0] ? routes[0] : ''
    ) as ChargerModel;
    const isValid = validAccessoryRoutes.includes(accessoryValue);

    return {
      value: accessoryValue,
      isValid,
    };
  }, [isAccessory, routes, validAccessoryRoutes]);

  // Helper function for consistent options
  const pushRing = useCallback(
    (subpath: string) => {
      if (isOuraRing4Wizard) {
        window.history.pushState(null, '', `${POP_PATHNAME}/${subpath}`);
      } else {
        window.history.pushState(
          null,
          '',
          `${POP_PATHNAME}/${RingModelEnum.Gen3}/${subpath}`,
        );
      }
    },
    [isOuraRing4Wizard],
  );

  const pushAccessory = useCallback(
    (value: ChargerModel) => {
      if (value !== accessory.value) {
        window.history.pushState(null, '', `${ACCESSORIES_ROOT_PATH}/${value}`);
      }
    },
    [accessory.value],
  );

  const pushAccessorySize = useCallback(
    (value: string) => {
      if (value !== size.value) {
        window.history.pushState(
          null,
          '',
          `${ACCESSORIES_ROOT_PATH}/${accessory.value}/${value}`,
        );
      }
    },
    [accessory.value, size.value],
  );

  const pushStyle = useCallback(
    (value: RingStyle) => {
      if (value !== style.value) {
        pushRing(value);
      }
    },
    [style.value, pushRing],
  );

  const pushFinish = useCallback(
    (value: RingFinish) => {
      if (value !== finish.value) {
        pushRing(`${style.value}/${value}`);
      }
    },
    [finish.value, pushRing, style.value],
  );

  const pushSize = useCallback(
    (value: string) => {
      if (value !== size.value) {
        pushRing(`${style.value}/${finish.value}/${value}`);
      }
    },
    [finish.value, pushRing, size.value, style.value],
  );

  // used to hide any page flicker while the route gets sorted out
  const isReady = style.isValid || finish.isValid || accessory.isValid;

  return {
    accessory,
    isAccessory,
    isOuraRing4Wizard,
    isReady,
    style,
    finish,
    size,
    pushAccessory,
    pushAccessorySize,
    pushStyle,
    pushFinish,
    pushSize,
  };
};

export default useCurrentRoute;
